import { Checkbox, Select, SelectProps } from "antd";
import { useAuth } from "../../Contexts/AuthContext";
import { WebApiMemberDtoMemberDto, WebApiModelsMemberField, WebApiServicesDataTransferObjectsGroupServiceGroupResultDto, WebApiServicesDataTransferObjectsMemberServiceFilterLogic } from "../../Repository/eventbookyapi";
import { MemberService } from "../../Services/MemberService";
import { TextService } from "../../Services/TextService";
import { EmailNotificationPreference } from "../../Types/EmailNotificationPreference";
import { FamilyMemberConfiguration } from "../../Types/FamilyMemberConfiguration";
import { FilterOperator } from "../../Types/FilterOperator";
import { MemberPaymentStatus } from "../../Types/MemberPaymentStatus";
import { PaymentConfiguration } from "../../Types/PaymentConfiguration";
import { SubscriptionLevel } from "../../Types/SubscriptionLevel";
import { filterByRecipientType, RecipientTypeFilter } from "../../Helpers/FamilyMemberHelper";
import { BookySelectPaymentStatus } from "../Forms/BookyPaymentStatusSelect";
import { AdvancedMemberFilter, FilterRequest } from "./AdvancedMemberFilter";

/** What the admin has chosen to build a send list of members from. */
export type RecipientFilters = {
    groupIds: number[],
    membershipProductIds: number[],
    paymentStatus?: MemberPaymentStatus,
    recipientType: RecipientTypeFilter,
    /** Also members who have said no to information e-mails. */
    includeNoEmailPreference: boolean,
    advanced?: FilterRequest
};

export const emptyRecipientFilters: RecipientFilters = {
    groupIds: [],
    membershipProductIds: [],
    recipientType: "all",
    includeNoEmailPreference: false
};

/**
 * Fetches the members the filters choose. Only members with an e-mail address are included, since the
 * list is used to send e-mails (mails under Kommunikation and invites to events).
 */
export const fetchRecipients = async (filters: RecipientFilters, familyMembersEnabled: boolean): Promise<WebApiMemberDtoMemberDto[]> => {
    const emailFilter = { fieldName: "Email", operator: FilterOperator.IsNotEmpty, value: undefined as string | undefined, logic: 0 as WebApiServicesDataTransferObjectsMemberServiceFilterLogic };
    const advancedFilters = (filters.advanced?.filters ?? []).map(filter => ({
        fieldName: filter.fieldName,
        operator: filter.operator,
        value: filter.value,
        logic: (filter.logic === 'Or' ? 1 : 0) as WebApiServicesDataTransferObjectsMemberServiceFilterLogic
    }));

    if (!advancedFilters.some(f => f.fieldName === "Email" && f.operator === FilterOperator.IsNotEmpty)) {
        advancedFilters.push(emailFilter);
    }

    let members = await MemberService.GetFilteredMembers({
        status: filters.paymentStatus,
        emailNotificationPreference: filters.includeNoEmailPreference ? EmailNotificationPreference.NoInformationEmails : EmailNotificationPreference.Unspecified,
        isAllMembers: filters.groupIds.length == 0 || filters.groupIds.some(p => p == -1),
        groupsIds: filters.groupIds,
        filters: advancedFilters,
        membershipProductIds: filters.membershipProductIds.length > 0 ? filters.membershipProductIds : undefined,
    });

    if (familyMembersEnabled) {
        members = filterByRecipientType(members, filters.recipientType);
    }

    return members;
};

/**
 * The filters for choosing members to send to: groups, membership, payment status, member type
 * (primaries / family members), the e-mail preference and the advanced filter. The same under
 * Kommunikation and when inviting to an event, so both offer the same choices.
 */
export const MemberRecipientFilters = (props: {
    groups: WebApiServicesDataTransferObjectsGroupServiceGroupResultDto[],
    membershipProducts?: any[],
    memberFieldSettings: WebApiModelsMemberField[],
    value: RecipientFilters,
    onChange: (value: RecipientFilters) => void
}) => {
    const GetText = TextService.GetText;
    const { user } = useAuth();
    const familyMembersEnabled = user?.familyMemberConfiguration === FamilyMemberConfiguration.Enabled;
    const canUseAdvancedFilter = user?.subscriptionLevel === SubscriptionLevel.Premium || user?.subscriptionLevel === SubscriptionLevel.Enterprise;
    const change = (part: Partial<RecipientFilters>) => props.onChange({ ...props.value, ...part });
    const selectStyle = { width: '100%', maxWidth: '400px', marginBottom: "10px" };

    const groupOptions: SelectProps['options'] = props.groups.map(group => ({ label: group.name, value: group.id }));

    return <>
        <Select placeholder={GetText("EmailForm_ChooseCategories")} value={props.value.groupIds} onChange={(groupIds: number[]) => change({ groupIds })} style={selectStyle} mode="multiple" options={groupOptions}></Select>
        {(props.membershipProducts?.length ?? 0) > 0 ? <div><Select
            placeholder={GetText("EmailForm_ChooseMembershipProducts")}
            mode="multiple"
            allowClear
            style={selectStyle}
            value={props.value.membershipProductIds}
            onChange={(membershipProductIds: number[]) => change({ membershipProductIds })}
            options={props.membershipProducts!.map((p: any) => ({ label: p.name, value: p.id }))}
        /></div> : ""}
        {user?.paymentConfiguration == PaymentConfiguration.YearlyPayments ? <div><BookySelectPaymentStatus showLastYearOptions={true} style={selectStyle} onSelectPaymentChanged={(paymentStatus: MemberPaymentStatus) => change({ paymentStatus })} /></div> : ""}
        {familyMembersEnabled ? <div style={{ marginBottom: "10px" }}>
            <span style={{ fontSize: 12, color: '#8c8c8c' }}>{GetText("Communication_RecipientTypeLabel")}</span>
            <Select
                value={props.value.recipientType}
                style={{ width: '100%', maxWidth: '400px', display: 'block' }}
                onChange={(recipientType: RecipientTypeFilter) => change({ recipientType })}
                options={[
                    { label: GetText("Communication_RecipientAll"), value: "all" },
                    { label: GetText("Communication_RecipientPrimariesOnly"), value: "primaries" },
                    { label: GetText("Communication_RecipientFamilyOnly"), value: "family" },
                ]}
            />
        </div> : ""}
        <div style={{ textAlign: "left" }}>
            <Checkbox checked={props.value.includeNoEmailPreference} onChange={(e) => change({ includeNoEmailPreference: e.target.checked })}>{GetText("EmailForm_OverrideEmailSetting")}</Checkbox>
        </div>
        {canUseAdvancedFilter ?
            <AdvancedMemberFilter
                memberFieldSettings={props.memberFieldSettings}
                onFilterChange={(advanced) => change({ advanced })}
                hideApplyButton={true}
            /> : null}
    </>;
};
